import { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  Briefcase,
  CheckCircle2,
  DollarSign,
  FileText,
  HandCoins,
  ShieldAlert,
  Truck,
  Users,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import { buildGerencialExportRows, createReportXlsxBlob, shouldShowReportExport } from './reportesExport.js';
import './ReportesGerenciales.css';
import {
  fetchReportesKpis,
  fetchReporteOperacion,
  fetchReporteFinanzas,
  fetchReporteClientes,
  fetchReporteChoferes,
} from '@/services/reportes.js';

const GERENCIAL_ROLES = new Set(['admin', 'gerente', 'gerencial', 'director', 'directivo']);

const formatter = new Intl.NumberFormat('es-AR');
const moneyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

const tabs = [
  { id: 'operacion', label: 'Operación', icon: Truck },
  { id: 'finanzas', label: 'Finanzas', icon: HandCoins },
  { id: 'clientes', label: 'Clientes', icon: Users },
  { id: 'choferes', label: 'Choferes / Flota', icon: Briefcase },
];

const formatNumber = (value) => formatter.format(Number(value || 0));
const formatMoney = (value) => moneyFormatter.format(Number(value || 0));

const mergeSeries = (left = [], right = []) => {
  const map = new Map();
  left.forEach((item) => {
    map.set(item.periodo, { periodo: item.periodo, facturado: Number(item.total || 0) });
  });
  right.forEach((item) => {
    const entry = map.get(item.periodo) || { periodo: item.periodo };
    entry.cobrado = Number(item.total || 0);
    map.set(item.periodo, entry);
  });
  return Array.from(map.values()).sort((a, b) => a.periodo.localeCompare(b.periodo));
};

function KPIItem({ icon: Icon, title, value, highlight }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-[rgba(var(--border),0.65)] bg-[rgba(var(--panel),0.9)] p-5 shadow-[var(--shadow)]">
      <div>
        <p className="text-sm font-medium text-[rgba(var(--muted),0.85)]">{title}</p>
        <p className="mt-2 text-2xl font-semibold text-[rgb(var(--fg))]">{value}</p>
        {highlight && <p className="mt-1 text-xs text-emerald-500">{highlight}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(var(--brand),0.15)] text-[rgb(var(--brand))]">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  );
}

function SummaryTable({ title, columns, rows }) {
  return (
    <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] shadow-[var(--shadow)]">
      <div className="border-b border-[rgba(var(--border),0.6)] px-5 py-3">
        <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[rgba(var(--border),0.2)] text-left text-[rgba(var(--muted),0.85)]">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-5 py-3 font-medium">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-5 py-5 text-center text-[rgba(var(--muted),0.7)]">
                  Sin datos para el período seleccionado.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={`${row.id || row.label || index}`}
                  className="border-t border-[rgba(var(--border),0.4)] text-[rgb(var(--fg))]"
                >
                  {Object.values(row).map((cell, cellIndex) => (
                    <td key={`${index}-${cellIndex}`} className="px-5 py-3">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ReportesGerenciales({ initialTab = 'operacion' }) {
  const { user, sucursal } = useAuth();
  const validTabs = new Set(tabs.map((tab) => tab.id));
  const [activeTab, setActiveTab] = useState(validTabs.has(initialTab) ? initialTab : 'operacion');
  const [filters, setFilters] = useState({
    fechaDesde: '',
    fechaHasta: '',
    sucursalId: sucursal?.id ? String(sucursal.id) : '',
    clienteId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [kpis, setKpis] = useState(null);
  const [operacion, setOperacion] = useState(null);
  const [finanzas, setFinanzas] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [choferes, setChoferes] = useState(null);

  const isGerencial = useMemo(() => {
    const role = String(user?.rol || '').toLowerCase();
    if (GERENCIAL_ROLES.has(role)) return true;
    const roles = user?.roles || [];
    return roles.some((r) => GERENCIAL_ROLES.has(String(r?.nombre || '').toLowerCase()));
  }, [user]);

  useEffect(() => {
    if (!isGerencial) return;
    let isMounted = true;
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const kpisData = await fetchReportesKpis(filters);
        if (!isMounted) return;
        setKpis(kpisData?.kpis || null);

        if (activeTab === 'operacion') {
          const operacionData = await fetchReporteOperacion(filters);
          if (!isMounted) return;
          setOperacion(operacionData || null);
        }
        if (activeTab === 'finanzas') {
          const finanzasData = await fetchReporteFinanzas(filters);
          if (!isMounted) return;
          setFinanzas(finanzasData || null);
        }
        if (activeTab === 'clientes') {
          const clientesData = await fetchReporteClientes(filters);
          if (!isMounted) return;
          setClientes(clientesData || null);
        }
        if (activeTab === 'choferes') {
          const choferesData = await fetchReporteChoferes(filters);
          if (!isMounted) return;
          setChoferes(choferesData || null);
        }
      } catch (err) {
        console.error('Error cargando reportes gerenciales', err);
        if (isMounted) {
          setError('No se pudieron cargar los reportes. Verifique filtros o permisos.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    run();
    return () => {
      isMounted = false;
    };
  }, [activeTab, filters, isGerencial]);

  const kpiItems = useMemo(() => {
    if (!kpis) return [];
    return [
      { title: 'Guías emitidas', value: formatNumber(kpis.guias_emitidas), icon: FileText },
      { title: 'Guías entregadas', value: formatNumber(kpis.guias_entregadas), icon: CheckCircle2 },
      { title: 'Guías retenidas', value: formatNumber(kpis.guias_retenidas), icon: ShieldAlert },
      { title: 'Total facturado', value: formatMoney(kpis.total_facturado), icon: DollarSign },
      { title: 'Total cobrado', value: formatMoney(kpis.total_cobrado), icon: HandCoins },
      { title: 'Pendiente de cobro', value: formatMoney(kpis.pendiente_cobro), icon: AlertTriangle },
    ];
  }, [kpis]);

  const finanzasSeries = useMemo(
    () => mergeSeries(finanzas?.facturacion_mensual || [], finanzas?.cobranzas_mensual || []),
    [finanzas],
  );

  const report = { operacion, finanzas, clientes, choferes }[activeTab];
  const exportRows = buildGerencialExportRows(activeTab, { ...(report || {}), kpis });
  const exportColumns = {
    operacion: ['section', 'indicador', 'valor', 'fecha', 'cantidad', 'estado', 'sucursal', 'total', 'entregadas'],
    finanzas: ['section', 'indicador', 'valor', 'periodo', 'facturado', 'cobrado', 'razon_social', 'total_facturado'],
    clientes: ['section', 'indicador', 'valor', 'razon_social', 'total_facturado', 'estado', 'cantidad'],
    choferes: ['section', 'indicador', 'valor', 'estado', 'cantidad', 'apellido', 'nombre', 'licencia_vencimiento'],
  }[activeTab] || ['section'];
  const exportTitle = tabs.find(tab => tab.id === activeTab)?.label || 'Reporte';
  const showExport = shouldShowReportExport({ loading, error, rows: exportRows });
  const downloadXlsx = () => {
    const blob = createReportXlsxBlob({ title: exportTitle, period: `${filters.fechaDesde || '—'} a ${filters.fechaHasta || '—'}`, columns: exportColumns, rows: exportRows });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = `reporte-${activeTab}.xlsx`; link.click(); URL.revokeObjectURL(url);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg))] text-[rgb(var(--fg))]">
      <div className="mx-auto w-full max-w-[1400px] px-6 pb-12 pt-6">
        <PageHeader
          title="Reportes Gerenciales"
          subtitle="Visión ejecutiva con KPIs operativos y financieros en tiempo real."
        />

        {!isGerencial && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200">
            Acceso restringido. Este módulo está disponible solo para roles gerenciales.
          </div>
        )}

        {isGerencial && (
          <>
            <section className="gerencial-report-filters mt-6 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.9)] p-4 shadow-[var(--shadow)]">
              <div className="grid gap-4 md:grid-cols-4">
                <div>
                  <label className="text-xs font-medium text-[rgba(var(--muted),0.85)]">
                    Fecha desde
                  </label>
                  <input
                    type="date"
                    value={filters.fechaDesde}
                    onChange={handleFilterChange('fechaDesde')}
                    className="mt-1 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[rgba(var(--muted),0.85)]">
                    Fecha hasta
                  </label>
                  <input
                    type="date"
                    value={filters.fechaHasta}
                    onChange={handleFilterChange('fechaHasta')}
                    className="mt-1 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[rgba(var(--muted),0.85)]">Sucursal</label>
                  <input
                    type="text"
                    placeholder="Sucursal ID"
                    value={filters.sucursalId}
                    onChange={handleFilterChange('sucursalId')}
                    className="mt-1 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[rgba(var(--muted),0.85)]">
                    Cliente (opcional)
                  </label>
                  <input
                    type="text"
                    placeholder="Cliente ID"
                    value={filters.clienteId}
                    onChange={handleFilterChange('clienteId')}
                    className="mt-1 w-full rounded-lg border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </section>

            <section className="mt-6 grid gap-4 lg:grid-cols-3">
              {kpiItems.map((item) => (
                <KPIItem key={item.title} {...item} />
              ))}
            </section>

            <section className="mt-8" data-report-navigation>
              <div className="flex flex-wrap gap-3">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
                        isActive
                          ? 'bg-[rgb(var(--brand))] text-white shadow-[var(--shadow)]'
                          : 'border border-[rgba(var(--border),0.6)] text-[rgb(var(--fg))]'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {showExport && <div className="mt-4 flex gap-2" data-report-actions>
                <button type="button" onClick={downloadXlsx} className="rounded-lg border px-4 py-2 text-sm">Exportar XLSX</button>
                <button type="button" onClick={() => window.print()} className="rounded-lg border px-4 py-2 text-sm">Imprimir / Guardar como PDF (desde el navegador)</button>
              </div>}
              {error && (
                <div className="mt-4 rounded-xl border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 dark:border-yellow-900/60 dark:bg-yellow-950/40 dark:text-yellow-200">
                  {error}
                </div>
              )}

              {loading && (
                <div className="mt-4 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.85)] p-3 text-sm text-[rgba(var(--muted),0.8)]">
                  Cargando reportes...
                </div>
              )}
            </section>

            <main className="gerencial-report-print" data-report-title={exportTitle} data-report-period={`${filters.fechaDesde || '—'} a ${filters.fechaHasta || '—'}`}>
            {activeTab === 'operacion' && (
              <section className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                  <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Guías por día</h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={operacion?.guias_por_dia || []}>
                        <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                        <XAxis dataKey="fecha" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="cantidad" stroke="rgb(var(--brand))" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                  <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Guías por estado</h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={operacion?.guias_por_estado || []}>
                        <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                        <XAxis dataKey="estado" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="cantidad" fill="rgb(var(--brand))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <SummaryTable
                  title="Resumen por sucursal"
                  columns={['Sucursal', 'Total guías', 'Entregadas']}
                  rows={(operacion?.guias_por_sucursal || []).map((item) => ({
                    label: item.sucursal || 'N/A',
                    total: formatNumber(item.total),
                    entregadas: formatNumber(item.entregadas),
                  }))}
                />
              </section>
            )}

            {activeTab === 'finanzas' && (
              <section className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                  <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Facturación vs cobranzas</h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={finanzasSeries}>
                        <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                        <XAxis dataKey="periodo" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="facturado" stroke="rgb(var(--brand))" strokeWidth={2} />
                        <Line type="monotone" dataKey="cobrado" stroke="#38bdf8" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <SummaryTable
                  title="Top clientes por facturación"
                  columns={['Cliente', 'Total facturado']}
                  rows={(finanzas?.top_clientes?.items || []).map((item) => ({
                    label: item.razon_social,
                    total: formatMoney(item.total_facturado),
                  }))}
                />
              </section>
            )}

            {activeTab === 'clientes' && (
              <section className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                  <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Ranking de clientes</h3>
                  <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(clientes?.ranking_facturacion?.items || []).slice(0, 8)}>
                        <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                        <XAxis dataKey="razon_social" hide />
                        <YAxis />
                        <Tooltip formatter={(value) => formatMoney(value)} />
                        <Bar dataKey="total_facturado" fill="rgb(var(--brand))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <SummaryTable
                  title="Clientes por estado"
                  columns={['Estado', 'Cantidad']}
                  rows={(clientes?.clientes_por_estado || []).map((item) => ({
                    label: item.estado || 'N/A',
                    total: formatNumber(item.cantidad),
                  }))}
                />
              </section>
            )}

            {activeTab === 'choferes' && (
              <section className="mt-6 grid gap-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                    <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Choferes por estado</h3>
                    <div className="mt-4 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={choferes?.choferes_por_estado || []}>
                          <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                          <XAxis dataKey="estado" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="cantidad" fill="rgb(var(--brand))" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.92)] p-4 shadow-[var(--shadow)]">
                    <h3 className="text-sm font-semibold text-[rgb(var(--fg))]">Flota por estado</h3>
                    <div className="mt-4 h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={choferes?.flota_por_estado || []}>
                          <CartesianGrid stroke="rgba(var(--border),0.3)" strokeDasharray="3 3" />
                          <XAxis dataKey="estado" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="cantidad" fill="#38bdf8" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <SummaryTable
                  title="Licencias próximas a vencer"
                  columns={['Chofer', 'Vencimiento']}
                  rows={(choferes?.licencias_por_vencer?.items || []).map((item) => ({
                    label: `${item.apellido || ''} ${item.nombre || ''}`.trim() || 'Sin nombre',
                    total: item.licencia_vencimiento || 'Sin fecha',
                  }))}
                />
              </section>
            )}
            </main>
          </>
        )}
      </div>
    </div>
  );
}
